// MIS report exports: Excel (.xlsx), CSV, PDF and a print view.
// Excel and CSV use each report's explicit exportColumns; PDF and Print carry the on-screen columns.
// The spreadsheet and PDF libraries load on first use so they stay out of the main bundle.
import { REPORTS, expiredDocumentFields, parseReportDate } from "./reportConfig";

export const EXPORT_FORMATS = {
  xlsx: { label: "Excel", extension: "xlsx", description: "Formatted workbook with filters and a frozen header" },
  csv: { label: "CSV", extension: "csv", description: "UTF-8 text, opens in any spreadsheet tool" },
  pdf: { label: "PDF", extension: "pdf", description: "Printable report with page numbers" },
  print: { label: "Print", extension: null, description: "Print only the report table" },
};

const REPORT_HEADING = "Police Verification — MIS Reports";
const pad = (n) => String(n).padStart(2, "0");

export const isoDate = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
export const formatGeneratedAt = (date) =>
  `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;

export const exportFileName = (tab, format, date = new Date()) =>
  `MIS_${REPORTS[tab].fileLabel}_Report_${isoDate(date)}.${EXPORT_FORMATS[format].extension}`;

export const cellValue = (row, column) => (column.value ? column.value(row) : row[column.field]);

const isBlank = (value) => value === undefined || value === null || String(value).trim() === "";
const asNumber = (value) => (isBlank(value) || Number.isNaN(Number(value)) ? null : Number(value));
export const cellText = (value) => (isBlank(value) ? "" : String(value));

// Expired validity dates print in red (#DC2626) in Excel, PDF and Print; CSV has no colour, so its
// Defaulter Reason column identifies them instead.
const EXPIRED_RGB = [220, 38, 38];
export const isExpiredCell = (row, column, today) =>
  Boolean(column.highlightExpired) && !isBlank(row[column.field]) && expiredDocumentFields(row, today).includes(column.field);

// ---- CSV ----------------------------------------------------------------------------

// Text starting with these characters could run as a spreadsheet formula when the file is opened.
const FORMULA_START = /^[=+\-@\t\r]/;

export const csvCell = (value, column) => {
  let text;
  if (column.type === "count" && asNumber(value) !== null) {
    text = String(asNumber(value));
  } else {
    text = cellText(value);
    if (FORMULA_START.test(text)) text = `'${text}`;
  }
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

// The byte-order mark lets Excel read Hindi and other Unicode text as UTF-8.
export const buildCsv = (rows, columns) => {
  const header = columns.map((c) => csvCell(c.header, { type: "text" })).join(",");
  const lines = rows.map((row) => columns.map((c) => csvCell(cellValue(row, c), c)).join(","));
  return "﻿" + [header, ...lines].join("\r\n");
};

// ---- Excel --------------------------------------------------------------------------

const XLSX_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const NAVY = "FF123047";

// Excel stores dates as UTC day serials, so build the date at UTC midnight to avoid a one-day shift.
const excelDate = (value) => {
  const date = parseReportDate(value);
  return date ? new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())) : null;
};

const xlsxCell = (value, column) => {
  if (isBlank(value)) return null;
  if (column.type === "count") return asNumber(value) ?? String(value);
  if (column.type === "date") return excelDate(value) ?? String(value);
  // Identifiers stay text so leading zeros and long digit strings survive.
  return String(value);
};

const metaLines = (meta) => [
  `Category: ${meta.title}`,
  `Generated: ${formatGeneratedAt(meta.generatedAt)}`,
  `Filters: ${meta.filters.length ? meta.filters.join("; ") : "None"}`,
  `Records: ${meta.count}`,
];

export const buildWorkbook = async (rows, columns, meta) => {
  const mod = await import("exceljs");
  const ExcelJS = mod.default ?? mod;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Police Verification Portal";
  workbook.created = meta.generatedAt;

  const lines = metaLines(meta);
  const headerRow = lines.length + 3; // title, meta lines, one blank row
  const sheet = workbook.addWorksheet(meta.title.slice(0, 31), {
    views: [{ state: "frozen", ySplit: headerRow, showGridLines: false }],
  });

  sheet.getCell("A1").value = `${REPORT_HEADING} — ${meta.title} Report`;
  sheet.getCell("A1").font = { bold: true, size: 14, color: { argb: NAVY } };
  lines.forEach((line, i) => {
    const cell = sheet.getCell(i + 2, 1);
    cell.value = line;
    cell.font = { size: 10, color: { argb: "FF64748B" } };
  });

  const header = sheet.getRow(headerRow);
  columns.forEach((column, i) => {
    const cell = header.getCell(i + 1);
    cell.value = column.header;
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } };
    cell.alignment = { vertical: "middle", horizontal: column.type === "count" ? "right" : "left" };
  });
  header.height = 20;

  rows.forEach((row, r) => {
    const excelRow = sheet.getRow(headerRow + 1 + r);
    columns.forEach((column, i) => {
      const cell = excelRow.getCell(i + 1);
      cell.value = xlsxCell(cellValue(row, column), column);
      if (column.type === "count" && typeof cell.value === "number") cell.numFmt = "#,##0";
      if (column.type === "date" && cell.value instanceof Date) cell.numFmt = "dd-mm-yyyy";
      if (column.bold) cell.font = { bold: true };
      if (isExpiredCell(row, column, meta.generatedAt)) cell.font = { color: { argb: "FFDC2626" } };
      if (r % 2 === 1) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF8FAFC" } };
      cell.border = { bottom: { style: "thin", color: { argb: "FFE2E8F0" } } };
    });
  });

  columns.forEach((column, i) => {
    const longest = rows.reduce((max, row) => Math.max(max, cellText(cellValue(row, column)).length), column.header.length);
    sheet.getColumn(i + 1).width = Math.min(Math.max(longest + 2, column.type === "date" ? 12 : 8), 50);
  });

  if (rows.length) sheet.autoFilter = { from: { row: headerRow, column: 1 }, to: { row: headerRow, column: columns.length } };
  return workbook;
};

const buildXlsxBlob = async (rows, columns, meta) => {
  const workbook = await buildWorkbook(rows, columns, meta);
  return new Blob([await workbook.xlsx.writeBuffer()], { type: XLSX_TYPE });
};

// ---- PDF ----------------------------------------------------------------------------

// jsPDF's built-in fonts cover Latin-1 only; Hindi (Devanagari) would print as garbage.
const NON_LATIN = /[^ -ÿ\t\r\n]/;
export const hasNonLatinText = (rows, columns, filters = []) =>
  filters.some((text) => NON_LATIN.test(text)) || rows.some((row) => columns.some((column) => NON_LATIN.test(cellText(cellValue(row, column)))));

const pdfText = (value) => cellText(value).replace(/—/g, "-");

export const buildPdf = async (rows, columns, meta) => {
  const [jspdfModule, autoTableModule] = await Promise.all([import("jspdf"), import("jspdf-autotable")]);
  const JsPDF = jspdfModule.jsPDF ?? jspdfModule.default;
  const autoTable = autoTableModule.autoTable ?? autoTableModule.default;

  const doc = new JsPDF({ orientation: columns.length > 5 ? "landscape" : "portrait", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 32;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.setTextColor(18, 48, 71);
  doc.text(pdfText(REPORT_HEADING), margin, 40);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(100, 116, 139);
  const meta1 = `${meta.title} Report  |  Generated ${formatGeneratedAt(meta.generatedAt)}  |  ${meta.count} records`;
  doc.text(pdfText(meta1), margin, 58);
  const filterText = doc.splitTextToSize(pdfText(`Filters: ${meta.filters.length ? meta.filters.join("; ") : "None"}`), pageWidth - margin * 2);
  doc.text(filterText, margin, 72);
  const startY = 72 + filterText.length * 11 + 6;

  const totalPagesToken = "{total_pages}";
  autoTable(doc, {
    startY,
    head: [columns.map((c) => c.header)],
    body: rows.length
      ? rows.map((row) =>
          columns.map((c) => (isExpiredCell(row, c, meta.generatedAt) ? { content: pdfText(cellValue(row, c)), styles: { textColor: EXPIRED_RGB } } : pdfText(cellValue(row, c))))
        )
      : [[{ content: "No records", colSpan: columns.length, styles: { halign: "center" } }]],
    showHead: "everyPage",
    theme: "grid",
    margin: { top: 40, left: margin, right: margin, bottom: 36 },
    styles: { font: "helvetica", fontSize: columns.length > 6 ? 8 : 9, cellPadding: 4, overflow: "linebreak", lineColor: [226, 232, 240], lineWidth: 0.5, textColor: [30, 41, 59] },
    headStyles: { fillColor: [18, 48, 71], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: Object.fromEntries(columns.map((c, i) => [i, c.type === "count" ? { halign: "right" } : {}])),
    didDrawPage: (data) => {
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(pdfText(`MIS Reports - ${meta.title}`), margin, pageHeight - 16);
      const label = `Page ${data.pageNumber} of ${totalPagesToken}`;
      doc.text(label, pageWidth - margin, pageHeight - 16, { align: "right" });
    },
  });
  if (typeof doc.putTotalPages === "function") doc.putTotalPages(totalPagesToken);
  return doc;
};

// ---- Print --------------------------------------------------------------------------

const escapeHtml = (value) =>
  cellText(value).replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));

export const buildPrintHtml = (rows, columns, meta) => {
  const landscape = columns.length > 5;
  const head = columns.map((c) => `<th${c.type === "count" ? ' class="num"' : ""}>${escapeHtml(c.header)}</th>`).join("");
  const body = rows.length
    ? rows
        .map((row) => `<tr>${columns.map((c) => `<td${c.type === "count" ? ' class="num"' : isExpiredCell(row, c, meta.generatedAt) ? ' class="expired"' : ""}>${escapeHtml(cellValue(row, c))}</td>`).join("")}</tr>`)
        .join("")
    : `<tr><td colspan="${columns.length}" class="empty">No records</td></tr>`;
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>${escapeHtml(`MIS ${meta.title} Report`)}</title>
<style>
@page { size: A4 ${landscape ? "landscape" : "portrait"}; margin: 12mm; @bottom-right { content: "Page " counter(page) " of " counter(pages); font-size: 9px; color: #64748B; } }
* { box-sizing: border-box; }
body { margin: 0; font-family: "Segoe UI", "Nirmala UI", "Noto Sans Devanagari", Mangal, Arial, sans-serif; color: #123047; font-size: 11px; }
h1 { font-size: 16px; margin: 0 0 4px; }
.meta { color: #64748B; margin: 0 0 2px; }
table { width: 100%; border-collapse: collapse; margin-top: 10px; }
thead { display: table-header-group; }
th, td { border: 1px solid #CBD5E1; padding: 4px 6px; text-align: left; vertical-align: top; overflow-wrap: anywhere; }
th { background: #EEF2F6; font-weight: 600; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
tr { break-inside: avoid; }
.num { text-align: right; font-variant-numeric: tabular-nums; }
.empty { text-align: center; color: #64748B; }
.expired { color: #DC2626; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
</style></head><body>
<h1>${escapeHtml(REPORT_HEADING)}</h1>
${metaLines(meta).map((line) => `<p class="meta">${escapeHtml(line)}</p>`).join("")}
<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
</body></html>`;
};

// Prints from a hidden frame, so the sidebar, toolbar and buttons never reach the page.
const printHtml = (html) =>
  new Promise((resolve, reject) => {
    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.title = "Print preview";
    Object.assign(frame.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0" });
    document.body.appendChild(frame);
    const win = frame.contentWindow;
    if (!win) {
      frame.remove();
      reject(new Error("The print view could not be opened."));
      return;
    }
    const cleanup = () => setTimeout(() => frame.remove(), 500);
    win.addEventListener("afterprint", cleanup, { once: true });
    win.document.open();
    win.document.write(html);
    win.document.close();
    setTimeout(() => {
      try {
        win.focus();
        win.print();
        resolve();
      } catch (err) {
        reject(err);
      } finally {
        setTimeout(() => frame.isConnected && frame.remove(), 60000);
      }
    }, 100);
  });

// ---- Entry point --------------------------------------------------------------------

export const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking straight away can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// Lets the progress indicator paint before the file is built.
const nextFrame = () => new Promise((resolve) => setTimeout(resolve, 0));

export async function exportReport({ format, tab, rows, filters = [], generatedAt = new Date() }) {
  const report = REPORTS[tab];
  const meta = { title: report.title, generatedAt, filters, count: rows.length };
  await nextFrame();

  if (format === "print") {
    await printHtml(buildPrintHtml(rows, report.columns, meta));
    return { fileName: null };
  }

  const fileName = exportFileName(tab, format, generatedAt);
  let blob;
  if (format === "xlsx") blob = await buildXlsxBlob(rows, report.exportColumns, meta);
  else if (format === "csv") blob = new Blob([buildCsv(rows, report.exportColumns)], { type: "text/csv;charset=utf-8" });
  else if (format === "pdf") {
    if (hasNonLatinText(rows, report.columns, filters)) throw new Error("These records contain Hindi or other Unicode text that the PDF generator cannot render. Use Print and choose “Save as PDF” instead.");
    blob = (await buildPdf(rows, report.columns, meta)).output("blob");
  } else throw new Error(`Unknown export format: ${format}`);

  downloadBlob(blob, fileName);
  return { fileName };
}
